import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Loader2,
  MessageCircle,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  User,
  X,
} from "lucide-react";
import { ApiError, send as apiSend } from "@/lib/api";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn, fmtInt } from "@/lib/utils";
import { track } from "@/lib/usage";
import { useConfirm } from "@/components/ui";

type Message = { role: "user" | "assistant"; content: string };

type ChatResp = {
  role: "assistant";
  content: string;
  model: string;
  usage: {
    inputTokens: number;
    outputTokens: number;
    usedInWindow: number;
  };
};

const SUGGESTIONS = [
  "Какой сайт растёт быстрее всех за месяц?",
  "Где CTR ниже среднего и почему?",
  "Перечисли сайты с проблемами индексации",
  "Какие топ-запросы в среднем по портфолио?",
  "Где CWV в красной зоне и что с этим делать?",
];

export default function ChatWidget() {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useLocalStorage<Message[]>(
    "chat.history",
    [],
  );
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const [lastUsage, setLastUsage] = useState<ChatResp["usage"] | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, busy, open]);

  // Retry-after countdown
  useEffect(() => {
    if (retryAfter <= 0) return;
    const t = setInterval(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [retryAfter]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (text?: string) => {
    const userText = (text ?? input).trim();
    if (!userText || busy) return;
    const newMessages: Message[] = [
      ...messages,
      { role: "user", content: userText },
    ];
    setMessages(newMessages);
    setInput("");
    setBusy(true);
    setError(null);
    track("chat.send_message");
    try {
      const reply = await apiSend<ChatResp>("/api/chat", "POST", {
        messages: newMessages,
      });
      setMessages([
        ...newMessages,
        { role: "assistant", content: reply.content },
      ]);
      setLastUsage(reply.usage);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        const d = e.body as { retryAfterSec?: number } | null;
        setError(e.message || "Лимит превышен");
        setRetryAfter(d?.retryAfterSec || 30);
      } else setError((e as Error).message);
      setMessages(messages);
      setInput(userText); // откат сообщения — вернуть текст в поле ввода
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full shadow-lg",
          "bg-gradient-to-br from-brand-400 to-brand-700 text-white",
          "flex items-center justify-center hover:scale-105 transition-transform",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2",
        )}
        title="ИИ-чат"
        aria-label="ИИ-чат"
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <MessageCircle className="h-5 w-5" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className={cn(
            "fixed bottom-20 right-4 z-50",
            "w-[calc(100vw-2rem)] sm:w-[380px]",
            "h-[min(560px,calc(100vh-6rem))]",
            "card flex flex-col shadow-xl overflow-hidden",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-ink-100 dark:border-ink-800 shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 grid place-items-center text-white">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <span className="font-semibold text-sm">
                ИИ-чат по всем сайтам
              </span>
            </div>
            <div className="flex items-center gap-1">
              {lastUsage && (
                <span className="text-[11px] text-ink-400 font-mono mr-1 tabular-nums">
                  {fmtInt(lastUsage.usedInWindow)} / 200k ткн/мин
                </span>
              )}
              {messages.length > 0 && (
                <button
                  type="button"
                  className="btn text-xs px-2 py-1"
                  title="Очистить историю"
                  aria-label="Очистить историю"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: "Очистить историю чата?",
                        danger: true,
                      })
                    ) {
                      setMessages([]);
                      setLastUsage(null);
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                className="btn px-2 py-1"
                onClick={() => setOpen(false)}
                aria-label="Закрыть"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Message list */}
          <div className="flex-1 overflow-auto px-4 py-4 space-y-4 min-h-0">
            {messages.length === 0 && (
              <div className="space-y-2">
                <div className="text-xs text-ink-500 dark:text-ink-400">
                  Примеры вопросов — нажмите, чтобы вставить:
                </div>
                <div className="flex flex-col gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="text-left text-xs rounded-lg border border-dashed border-ink-200 dark:border-ink-700 p-2.5 hover:border-brand-300 hover:bg-brand-600/5 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  "flex gap-2",
                  m.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                {m.role === "assistant" && (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 grid place-items-center shrink-0 text-white mt-0.5">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                )}
                <div
                  className={cn(
                    "rounded-2xl px-3 py-2 max-w-[85%] text-sm",
                    m.role === "user"
                      ? "bg-brand-600 text-white"
                      : "bg-ink-100 dark:bg-ink-800/60 text-ink-900 dark:text-ink-50",
                  )}
                >
                  {m.role === "assistant" ? (
                    <article className="prose-ai text-xs">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {m.content}
                      </ReactMarkdown>
                    </article>
                  ) : (
                    <div className="whitespace-pre-wrap text-xs">
                      {m.content}
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="h-7 w-7 rounded-full bg-ink-200 dark:bg-ink-700 grid place-items-center shrink-0 mt-0.5">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 grid place-items-center shrink-0 text-white">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </div>
                <div className="rounded-2xl px-3 py-2 bg-ink-100 dark:bg-ink-800/60 text-xs text-ink-500 dark:text-ink-400">
                  Думаю…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Error banner */}
          {error && (
            <div
              role="alert"
              className="px-3 py-2 border-t border-rose-200 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/30 text-xs text-rose-700 dark:text-rose-300 flex items-center gap-2 shrink-0"
            >
              <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 min-w-0 truncate">{error}</span>
              {retryAfter > 0 && (
                <span className="font-mono shrink-0">{retryAfter}s</span>
              )}
            </div>
          )}

          {/* Input row */}
          <div className="px-3 py-2.5 border-t border-ink-100 dark:border-ink-800 flex items-end gap-2 shrink-0">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                retryAfter > 0
                  ? `Подождите ${retryAfter} сек…`
                  : "Спросите про сайты… (Enter — отправить)"
              }
              rows={2}
              disabled={busy || retryAfter > 0}
              className="input flex-1 resize-none font-sans text-sm"
            />
            <button
              type="button"
              className={cn(
                "btn-primary h-10 shrink-0",
                (busy || retryAfter > 0) && "opacity-70 cursor-not-allowed",
              )}
              onClick={() => send()}
              disabled={busy || retryAfter > 0 || !input.trim()}
              aria-label="Отправить"
              title="Отправить"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
